import { useEffect, useRef, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { getUsername, getLanguageCode, getUserRoles, getRoleLabel, getTenantName } from '../../utils/auth';
import { endSession } from '../../utils/session';
import { NotificationBell } from './NotificationBell';
import { TeamChatPanel } from './TeamChatPanel';
import { AppInstallItem } from './AppInstallItem';
import './TopbarActions.css';

const LANG_TO_COUNTRY = { 'tr-TR': 'TR', 'en-US': 'US', 'de-DE': 'DE', 'fr-FR': 'FR' };
const LANGUAGE_OPTIONS = [
  { value: 'tr-TR', label: 'Türkçe', country: 'TR' },
];

// Tema modu: varsayılan 'system'; kullanıcının seçimi localStorage'da hatırlanır.
// Tema DEĞİŞTİRME kontrolü artık header'daki güneş/ay ikonudur (aşağıda) — avatar
// popover'ından TEMA bölümü kaldırıldı.
const themeStorageKey = 'datascope-theme-mode';

const languageStorageKey = 'datascope-language';

function initialsFrom(name) {
  if (!name) return '?';
  const parts = name.replace(/[._-]+/g, ' ').trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + second).toUpperCase() || '?';
}

function resolveTheme(mode) {
  if (mode === 'dark' || mode === 'light') return mode;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </svg>
  );
}

export function TopbarActions() {
  const [themeMode, setThemeMode] = useState(() => localStorage.getItem(themeStorageKey) || 'system');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolveTheme(themeMode));
    document.documentElement.setAttribute('data-theme-mode', themeMode);
    localStorage.setItem(themeStorageKey, themeMode);
  }, [themeMode]);

  useEffect(() => {
    const handler = (e) => {
      // Menu icindeki bazi bilesenler (AppInstallItem) modallerini createPortal ile
      // document.body'ye basar; bu DOM menunun disindadir. Muaf tutulmazsa modala
      // yapilan her tiklama "disari tiklandi" sayilir, menu kapanir, bilesen unmount
      // olur ve modal kaybolur. Link tiklamalarinda ise <a> tiklama tamamlanmadan
      // silindigi icin indirme hic baslamaz.
      if (e.target?.closest?.('[data-app-install-modal]')) return;
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Çıkış tek noktadan (utils/session) yürür: storage temizliği + tam reload. Buradan
  // elle localStorage.removeItem yapma — modül cache'leri ayakta kalır ve veri karışır.
  const handleLogout = () => endSession('manual', { source: 'topbar' });

  const handleLanguageChange = (langValue) => {
    localStorage.setItem(languageStorageKey, langValue);
    window.dispatchEvent(new Event('language:change'));
    // LocalizationContext yeniden okumayı tetiklemezse reload — basit ve garantili.
    window.location.reload();
  };

  const username = getUsername() || 'Guest';
  const initials = initialsFrom(username);
  const roles = getUserRoles();
  const role = roles.length > 0 ? getRoleLabel(roles[0]) : 'Sales Admin';
  const tenant = getTenantName();
  const languageCode = getLanguageCode();
  const countryCode = LANG_TO_COUNTRY[languageCode] ?? null;

  const resolvedTheme = resolveTheme(themeMode);

  return (
    <div className="topbar-actions" ref={wrapperRef}>
      <button
        type="button"
        className="topbar-theme-toggle"
        title={resolvedTheme === 'dark' ? 'Açık temaya geç' : 'Koyu temaya geç'}
        aria-label={resolvedTheme === 'dark' ? 'Açık temaya geç' : 'Koyu temaya geç'}
        onClick={() => setThemeMode(resolvedTheme === 'dark' ? 'light' : 'dark')}
      >
        {resolvedTheme === 'dark' ? <SunIcon /> : <MoonIcon />}
      </button>

      {/* TEK çan: bildirimler (/api/notifications, kişiye özel, okundu/okunmadı) + duyurular
          (/api/announcements, herkese açık süreli ilan) aynı panelde iki bölüm hâlinde.
          Önceden yan yana iki özdeş çan ikonu vardı ve hangisinin ne olduğu ayırt edilemiyordu. */}
      <NotificationBell />

      {/* #44 Ekip mesajlaşması — yan panel. Çandan AYRI bir ikon olarak durur: çan tek yönlü
          HABERDİR (bildirim + duyuru, "okundu işaretle" ile tüketilir), bu ise karşılıklı
          YAZIŞMADIR. Aynı panele katılsaydı ikisinin aksiyonları birbirine karışırdı.
          Kullanıcı organizasyon şemasında değilse bileşen HİÇ ÇİZİLMEZ. */}
      <TeamChatPanel />

      <button
        type="button"
        className="topbar-action-avatar"
        title={username}
        aria-label={`${username} hesabı`}
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        {initials}
      </button>

      {open && (
        <div className="topbar-account-popover" role="dialog" aria-label="Hesap">
          <div className="topbar-account-header">
            <div className="topbar-account-avatar-large" aria-hidden="true">{initials}</div>
            <div className="topbar-account-meta">
              <strong>{username}</strong>
              <small>{role}</small>
              {tenant && <span className="topbar-account-tenant">{tenant}</span>}
            </div>
          </div>

          <div className="topbar-account-section">
            <div className="topbar-account-section-label">DİL</div>
            <div className="topbar-account-lang-group" role="radiogroup" aria-label="Dil">
              {LANGUAGE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={opt.value === languageCode}
                  className={`topbar-account-lang-option${opt.value === languageCode ? ' active' : ''}`}
                  onClick={() => handleLanguageChange(opt.value)}
                >
                  <ReactCountryFlag countryCode={opt.country} svg style={{ width: '1.1em', height: '1.1em' }} />
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="topbar-account-divider" />

          <AppInstallItem />

          <button
            type="button"
            className="topbar-account-logout"
            onClick={handleLogout}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <path d="M16 17l5-5-5-5" />
              <path d="M21 12H9" />
            </svg>
            <span>Çıkış Yap</span>
            {countryCode && (
              <span className="topbar-account-logout-spacer">
                <ReactCountryFlag countryCode={countryCode} svg style={{ width: '0.95em', height: '0.95em', opacity: 0.6 }} />
              </span>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
